import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    Avatar,
    ListItem,
    ListItemText,
    Button,
    Typography,
    Box,
    Stack,
    Tooltip,
    Divider,
    Paper,
    useMediaQuery,
    useTheme
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { getInitials } from '../utils';
import { toZonedTime,  format as formatTz } from 'date-fns-tz';

const MatchListItem = ({ 
    match, 
    skipper, 
    opp, 
    onInterestClick, 
    placeIdToName,
    isInvitation = false
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showFullNote, setShowFullNote] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const renderAvatar = (skipper) => {
        if (!skipper) return null;
        
        const avatar = skipper.hasPicture ? (
            <Avatar 
                src={skipper.profilePictureUrl} 
                alt={skipper?.firstName + " " + skipper?.lastName}
                sx={{ width: 56, height: 56 }}
            />
        ) : (
            <Avatar sx={{ width: 56, height: 56 }}>
                {getInitials(`${skipper?.firstName || ''} ${skipper?.lastName || ''}`.trim())}
            </Avatar>
        );
        
        return (
            <Link to={`/profile/${skipper.userId}`} style={{ textDecoration: 'none' }}>
                {avatar}
            </Link>
        );
    };

    // Control buttons component
    const ControlButtons = () => (
        <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexDirection: 'row'
        }}>
            <Button
                variant={match.response === 1 ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => onInterestClick(match._id, match.response === 1 ? 0 : 1)}
                sx={{ 
                    fontSize: isMobile ? '0.7rem' : '0.75rem', 
                    textTransform: 'none',
                    padding: isMobile ? '2px 8px' : '4px 10px',
                    minWidth: isMobile ? '60px' : '64px',
                    height: isMobile ? 'auto' : '28px',
                    lineHeight: isMobile ? 'normal' : '1'
                }}
            >
                {t('match.interested')}
            </Button>
            
            <Button
                variant={match.response === 2 ? "contained" : "outlined"}
                color="error"
                size="small"
                onClick={() => onInterestClick(match._id, match.response === 2 ? 0 : 2)}
                sx={{
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    textTransform: 'none',
                    padding: isMobile ? '2px 8px' : '4px 10px',
                    minWidth: isMobile ? '60px' : '64px',
                    height: isMobile ? 'auto' : '28px',
                    lineHeight: isMobile ? 'normal' : '1',
                    whiteSpace: 'nowrap'
                }}
            >
                {t('match.markNotInterested')}
            </Button>

            {skipper && (
                <Tooltip title={t('messages.message')}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/messages/${skipper.userId}`)}
                        sx={{
                            minWidth: 0,
                            padding: isMobile ? '2px 8px' : '4px 8px',
                            height: isMobile ? 'auto' : '28px'
                        }}
                    >
                        <ChatBubbleOutlineIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                </Tooltip>
            )}
        </Box>
    );

    return (
        <Paper
            elevation={1}
            sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                marginBottom: '8px',
                overflow: 'hidden'
            }}
        >
            {/* Header section */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                padding: isMobile ? 1.5 : 2,
                backgroundColor: '#f9f9f9'
            }}>
                {/* Top row for mobile / Full row for desktop */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    width: '100%'
                }}>
                    {/* Part 1: Skipper's avatar */}
                    <Box sx={{ mr: isMobile ? 1.5 : 2 }}>
                        {renderAvatar(skipper)}
                    </Box>
                    
                    {/* Part 2: Opportunity name and posted by */}
                    <Box sx={{ flexGrow: 1 }}>
                        {(opp.name || skipper?.firstName || skipper?.lastName) && 
                            <Box>
                                <Typography 
                                    variant={isMobile ? "h6" : "h5"} 
                                    component="div"
                                    sx={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}
                                >
                                    {opp.name 
                                        ? (opp.name.length > 50 
                                            ? `${opp.name.substring(0, 50)}...` 
                                            : opp.name) 
                                        : t('match.opportunity')}
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                >
                                    {isInvitation ? t('match.invitedBy') : t('match.postedBy')} {' '}
                                    {skipper && (
                                        <Link 
                                            to={`/profile/${skipper.userId}`} 
                                            style={{ 
                                                textDecoration: 'none', 
                                                color: theme.palette.primary.main 
                                            }}
                                        >
                                            {skipper.firstName} {skipper.lastName}
                                        </Link>
                                    )}
                                    {isInvitation && !match.response ? ` - ${t('match.youAreInvited')}` : ""}
                                    {match.response === 1 ? ` - ${t('match.youAreInterested')}` : ""} 
                                    {match.response === 2 ? ` - ${t('match.youAreNotInterested')}` : ""}
                                </Typography>
                            </Box>
                        }
                    </Box>
                    
                    {/* Part 3: Control buttons (desktop only) */}
                    {!isMobile && (
                        <Box>
                            <ControlButtons />
                        </Box>
                    )}
                </Box>
                
                {/* Bottom row for mobile - Control buttons */}
                {isMobile && (
                    <Box sx={{ mt: 1, ml: 8.5 }}>
                        <ControlButtons />
                    </Box>
                )}
            </Box>
            
            <Divider />
            
            {/* Content section */}
            <Box sx={{ p: isMobile ? 1.5 : 2 }}>
                {/* Date and time */}
                <Stack 
                    direction="row" 
                    spacing={isMobile ? 1 : 2} 
                    sx={{ mb: 1 }}
                >
                    <Typography 
                        variant="subtitle1"
                        sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                    >
                        {formatTz(toZonedTime(opp.dateTime, opp.tz), "PPP", { timeZone: opp.tz })}
                        {/* {format(parseISO(opp.dateTime), 'PPP')} */}
                    </Typography>
                    <Typography 
                        variant="subtitle1"
                        sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                    >
                         {formatTz(toZonedTime(opp.dateTime, opp.tz), "p", { timeZone: opp.tz })}
                        {/* @{format(parseISO(opp.dateTime), 'p')} */}
                    </Typography>
                </Stack>
                
                {/* Location */}
                <Typography 
                    component="div" 
                    variant="body2" 
                    color="textPrimary" 
                    sx={{ 
                        mb: 0.5,
                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                    }}
                >
                     {t(`opportunity.form.${opp.type}`)} - {placeIdToName(opp.place)}
                </Typography>
                
                {/* Opportunity type */}
                <Typography 
                    component="div" 
                    variant="body2" 
                    sx={{ 
                        mb: 1,
                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                    }}
                >
                   
                </Typography>
                
                {/* Note */}
                {opp.note && (
                    <Box sx={{ mt: 1 }}>
                        <Typography 
                            component="div" 
                            variant="body2" 
                            color="textSecondary"
                            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                        >
                            {opp.note.length > 100
                                ? showFullNote
                                    ? opp.note
                                    : `${opp.note.substring(0, 100)}... `
                                : opp.note}
                        </Typography>
                        {opp.note.length > 100 && (
                            <Typography
                                component="span"
                                variant="body2"
                                color="primary"
                                sx={{ 
                                    cursor: 'pointer', 
                                    display: 'inline-block',
                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowFullNote(!showFullNote);
                                }}
                            >
                                {showFullNote ? t('match.showLess') : t('match.readMore')}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default MatchListItem;
